/**
 * Dual-range "logical button" comparison presets. Range A = the earlier/baseline period,
 * Range B = the later/current period, so a growing metric shows a positive Δ (B − A) in
 * green. Reuses the app's single-range preset helpers where possible.
 * For Media Devoted | Created By Ciro Autiero
 */

import { getRangeFromPreset } from '@/components/date-range';
import type { StatsDateRangePreset } from '@/components/date-range';
import { addDaysToIso, todayIsoDateParis } from '@/features/smswiser-campaigns/utils/dateFormat';

import type { DashboardDateRange } from '../data/types';

export type ComparePresetCode =
  | 'yesterday-vs-today'
  | 'last-week-vs-this-week'
  | 'last-month-vs-this-month'
  | 'last-year-vs-this-year'
  | 'custom';

export interface ComparePreset {
  code: ComparePresetCode;
  label: string;
}

export const COMPARE_PRESETS: ComparePreset[] = [
  { code: 'yesterday-vs-today', label: 'Yesterday → Today' },
  { code: 'last-week-vs-this-week', label: 'Last week → This week' },
  { code: 'last-month-vs-this-month', label: 'Last month → This month' },
  { code: 'last-year-vs-this-year', label: 'Last year → This year' },
  { code: 'custom', label: 'Custom' },
];

export const DEFAULT_COMPARE_PRESET: ComparePresetCode = 'last-month-vs-this-month';

const MONTHS = 'Jan Feb Mar Apr May Jun Jul Aug Sep Oct Nov Dec'.split(' ');

function mondayOfIso(iso: string): string {
  const dow = (new Date(`${iso}T12:00:00.000Z`).getUTCDay() + 6) % 7;
  return addDaysToIso(iso, -dow);
}

/** Monday-start week window for an offset (0 = this week clamped to today, -1 = last week). */
export function weekRange(offsetWeeks: number): DashboardDateRange {
  const today = todayIsoDateParis();
  const from = addDaysToIso(mondayOfIso(today), offsetWeeks * 7);
  const to = offsetWeeks === 0 ? today : addDaysToIso(from, 6);
  return { from, to };
}

/** The equal-length window immediately preceding `range` (for a custom "vs previous"). */
export function previousPeriod(range: DashboardDateRange): DashboardDateRange {
  const a = new Date(`${range.from}T12:00:00.000Z`).getTime();
  const b = new Date(`${range.to}T12:00:00.000Z`).getTime();
  const lenDays = Math.round((b - a) / 86_400_000) + 1;
  const to = addDaysToIso(range.from, -1);
  return { from: addDaysToIso(to, -(lenDays - 1)), to };
}

export function buildComparePreset(
  code: ComparePresetCode,
): { a: DashboardDateRange; b: DashboardDateRange } | null {
  if (code === 'last-week-vs-this-week') {
    return { a: weekRange(-1), b: weekRange(0) };
  }
  const pair: Record<string, [string, string]> = {
    'yesterday-vs-today': ['yesterday', 'today'],
    'last-month-vs-this-month': ['last-month', 'this-month'],
    'last-year-vs-this-year': ['last-year', 'this-year'],
  };
  const codes = pair[code];
  if (!codes) return null;
  const a = getRangeFromPreset(codes[0] as StatsDateRangePreset);
  const b = getRangeFromPreset(codes[1] as StatsDateRangePreset);
  return a && b ? { a, b } : null;
}

function dm(iso: string): string {
  if (!iso) return '';
  const d = new Date(`${iso}T12:00:00.000Z`);
  return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]}`;
}

/** Compact range label for the table headers, e.g. "1 Jun – 30 Jun" or "6 Jul". */
export function formatRangeShort(range: DashboardDateRange): string {
  if (!range.from || !range.to) return '—';
  if (range.from === range.to) return dm(range.from);
  return `${dm(range.from)} – ${dm(range.to)}`;
}

/** Reverse lookup: which preset (if any) produces `current` (=b) vs `baseline` (=a). */
export function matchComparePreset(
  current: DashboardDateRange,
  baseline: DashboardDateRange,
): ComparePresetCode {
  for (const p of COMPARE_PRESETS) {
    if (p.code === 'custom') continue;
    const built = buildComparePreset(p.code);
    if (
      built &&
      built.b.from === current.from &&
      built.b.to === current.to &&
      built.a.from === baseline.from &&
      built.a.to === baseline.to
    ) {
      return p.code;
    }
  }
  return 'custom';
}
